import React, { useState, useEffect, useContext } from "react";
import qs from "query-string";
import * as Yup from "yup";
import { useHistory } from "react-router-dom";
import { Link as RouterLink } from "react-router-dom";
import { toast } from "react-toastify";
import { Formik, Form, Field } from "formik";

import Avatar from "@material-ui/core/Avatar";
import Button from "@material-ui/core/Button";
import CssBaseline from "@material-ui/core/CssBaseline";
import TextField from "@material-ui/core/TextField";
import Link from "@material-ui/core/Link";
import Grid from "@material-ui/core/Grid";
import Box from "@material-ui/core/Box";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import Modal from "@material-ui/core/Modal";
import Backdrop from "@material-ui/core/Backdrop";
import Fade from "@material-ui/core/Fade";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import CardActions from "@material-ui/core/CardActions";
import CardHeader from "@material-ui/core/CardHeader";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import usePlans from "../../hooks/usePlans";
import { i18n } from "../../translate/i18n";

import { openApi } from "../../services/api";
import toastError from "../../errors/toastError";

import { Helmet } from "react-helmet";
import IconButton from "@material-ui/core/IconButton";
import Brightness4Icon from "@material-ui/icons/Brightness4";
import Brightness7Icon from "@material-ui/icons/Brightness7";
import CloseIcon from "@material-ui/icons/Close";
import ColorModeContext from "../../layout/themeContext";
import CircularProgress from "@material-ui/core/CircularProgress";
import useSettings from "../../hooks/useSettings"; // Importação adicionada
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  paper: {
    marginTop: theme.spacing(8),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(4),
    borderRadius: "12px",
    boxShadow: theme.shadows[5],
    position: "relative",
  },
  avatar: {
    margin: theme.spacing(1),
    backgroundColor: theme.palette.primary.main,
  },
  form: {
    width: "100%",
    marginTop: theme.spacing(3),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.getContrastText(theme.palette.primary.main),
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
    borderRadius: "25px",
    padding: theme.spacing(1.5),
  },
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  modalContent: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: "12px",
    padding: theme.spacing(4),
    boxShadow: theme.shadows[5],
    width: "80%",
    maxWidth: "800px",
    maxHeight: "90vh",
    overflowY: "auto",
    color: theme.palette.text.primary,
    position: "relative",
  },
  loadingModalContent: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: "12px",
    padding: theme.spacing(4),
    boxShadow: theme.shadows[5],
    width: "80%",
    maxWidth: "400px",
    maxHeight: "90vh",
    overflowY: "auto",
    color: theme.palette.text.primary,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  modalTitle: {
    marginBottom: theme.spacing(3),
    textAlign: "center",
    color: theme.palette.text.primary,
  },
  closeButton: {
    position: "absolute",
    top: theme.spacing(1),
    right: theme.spacing(1),
    color: theme.palette.grey[500],
  },
  card: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    border: `2px solid transparent`,
    transition: "border-color 0.3s, box-shadow 0.3s",
    position: "relative",
    "&:hover": {
      borderColor: theme.palette.primary.main,
      boxShadow: theme.shadows[3],
    },
  },
  selectedCard: {
    borderColor: theme.palette.secondary.main,
    boxShadow: theme.shadows[6],
  },
  cardHeader: {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.getContrastText(theme.palette.primary.light),
  },
  cardContent: {
    flexGrow: 1,
  },
  cardActions: {
    justifyContent: "center",
  },
  selectedPlan: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: "8px",
    textAlign: "center",
  },
  choosePlanButton: {
    marginTop: theme.spacing(2),
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.getContrastText(theme.palette.secondary.main),
    "&:hover": {
      backgroundColor: theme.palette.secondary.dark,
    },
    borderRadius: "25px",
    padding: theme.spacing(1),
  },
  checkIcon: {
    color: theme.palette.secondary.main,
    position: "absolute",
    top: theme.spacing(1),
    right: theme.spacing(1),
  },
  cardContainer: {
    position: "relative",
  },
  textField: {
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: theme.palette.grey[500],
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
    "& .MuiInputLabel-root": {
      color: theme.palette.text.primary,
    },
    "& .MuiInputBase-input": {
      color: theme.palette.text.primary,
    },
    "& .MuiFormHelperText-root": {
      color: theme.palette.error.main,
    },
  },
  iconButton: {
    position: "absolute",
    top: theme.spacing(1),
    right: theme.spacing(1),
    color: theme.palette.text.primary,
  },
  checkboxLabel: {
    display: "flex",
    alignItems: "center",
  },
}));

const phoneRegex = /^55\d{10,11}$/;

const UserSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Muito curto!")
    .max(50, "Muito longo!")
    .required("Obrigatório"),
  companyName: Yup.string()
    .min(2, "Muito curto!")
    .max(50, "Muito longo!")
    .required("Obrigatório"),
  password: Yup.string()
    .min(5, "Muito curta!")
    .max(50, "Muito longa!")
    .required("Obrigatório"),
  email: Yup.string()
    .email("Email inválido")
    .matches(/^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i, "É obrigatório cadastrar um e-mail válido.")
    .test('no-uppercase', 'Não é permitido letras maiúsculas no e-mail.', value => !/[A-Z]/.test(value || ''))
    .required("Obrigatório"),
  phone: Yup.string()
    .matches(phoneRegex, "Informe um número de WhatsApp válido (ex: 5511999999999)")
    .required("Obrigatório"),
  planId: Yup.string().required("Escolha um plano"),
  termsAccepted: Yup.bool()
    .oneOf([true], "Você deve aceitar os termos de uso")
    .required("Obrigatório"),
});

const SignUp = () => {
  const classes = useStyles();
  const history = useHistory();
  const { getPlanList } = usePlans();
  const { getPublicSetting } = useSettings(); // Extração da função do hook useSettings
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [openPlanModal, setOpenPlanModal] = useState(false);
  const [openTermsModal, setOpenTermsModal] = useState(false);
  const [isValidating, setIsValidating] = useState(false);

  const { colorMode } = useContext(ColorModeContext);
  const { appLogoFavicon, appName, mode } = colorMode;

  const systemName = appName || "ChatEnvio";
  const params = qs.parse(window.location.search);
  const companyId = params.companyId || null;

  const initialState = {
    name: "",
    email: "",
    password: "",
    phone: "",
    companyId,
    companyName: "",
    planId: "",
    termsAccepted: false,
  };

  useEffect(() => {
    // Verifica se a criação de usuários está habilitada
    getPublicSetting("userCreation")
      .then((data) => {
        if (data === "disabled") {
          toast.error(i18n.t("signup.toasts.disabled"));
          history.push("/login");
        }
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
  }, []); // Array vazio: executa apenas na montagem

  useEffect(() => {
    const fetchData = async () => {
      try {
        const planList = await getPlanList({ listPublic: "false" });
        setPlans(planList);
      } catch (error) {
        toastError(error);
      }
    };
    fetchData();
  }, []); // Array vazio: executa apenas na montagem


  const handleSignUp = async (values) => {
    setIsValidating(true);
    
    try {
      // Cadastra diretamente sem validação de WhatsApp
      await openApi.post("/auth/signup", values);
      toast.success(i18n.t("signup.toasts.success"));
      history.push("/login");
    } catch (err) {
      toastError(err);
    } finally {
      setIsValidating(false);
    }
  };

  const handleOpenPlanModal = () => {
    setOpenPlanModal(true);
  };

  const handleClosePlanModal = () => {
    setOpenPlanModal(false);
  };

  const handleOpenTermsModal = () => {
    setOpenTermsModal(true);
  };

  const handleCloseTermsModal = () => {
    setOpenTermsModal(false);
  };

  return (
    <Container component="main" maxWidth="sm">
      <CssBaseline />
      <Helmet>
        <title>{systemName}</title>
        <link rel="icon" href={appLogoFavicon || "/default-favicon.ico"} />
      </Helmet>

      <IconButton className={classes.iconButton} onClick={colorMode.toggleColorMode}>
        {mode === "dark" ? <Brightness7Icon /> : <Brightness4Icon />}
      </IconButton>

      <div className={classes.paper}>
        <Avatar className={classes.avatar}>
          <LockOutlinedIcon />
        </Avatar>
        <Typography component="h1" variant="h4" gutterBottom>
          {i18n.t("signup.title")}
        </Typography>
        <Formik
          initialValues={initialState}
          validationSchema={UserSchema}
          validate={values => {
            const errors = {};
            // Bloquear letras maiúsculas no e-mail
            if (values.email && /[A-Z]/.test(values.email)) {
              errors.email = "Não é permitido letras maiúsculas no e-mail.";
            }
            // Bloquear caracteres especiais na senha
            if (values.password && /[^A-Za-z0-9]/.test(values.password)) {
              errors.password = "Proibido caracteres especiais.";
            }
            if (values.phone && !phoneRegex.test(values.phone)) {
              errors.phone = "Informe um número de WhatsApp válido (ex: 5511999999999)";
            }
            return errors;
          }}
          onSubmit={async (values, actions) => {
            await handleSignUp(values);
            actions.setSubmitting(false);
          }}
        >
          {({ touched, errors, isSubmitting, setFieldValue, values, isValid }) => {
            const handlePlanSelect = (plan) => {
              setSelectedPlan(plan);
              setFieldValue("planId", plan.id);
              setOpenPlanModal(false);
            };
            // Função para forçar o e-mail para minúsculo
            const handleEmailChange = (e) => {
              const lower = e.target.value.toLowerCase();
              setFieldValue("email", lower);
            };
            // Função para bloquear caracteres especiais na senha
            const handlePasswordChange = (e) => {
              setFieldValue("password", e.target.value);
            };
            const handlePhoneChange = (e) => {
              // Permite apenas números
              const onlyNumbers = e.target.value.replace(/\D/g, "");
              setFieldValue("phone", onlyNumbers);
            };
            return (
              <>
                <Form className={classes.form}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <Field
                        as={TextField}
                        variant="outlined"
                        fullWidth
                        id="companyName"
                        label="Nome da Empresa"
                        name="companyName"
                        autoComplete="companyName"
                        error={touched.companyName && Boolean(errors.companyName)}
                        helperText={touched.companyName && errors.companyName}
                        className={classes.textField}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Field
                        as={TextField}
                        variant="outlined"
                        fullWidth
                        id="name"
                        label={i18n.t("signup.form.name")}
                        name="name"
                        autoComplete="name"
                        error={touched.name && Boolean(errors.name)}
                        helperText={touched.name && errors.name}
                        className={classes.textField}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" style={{ marginBottom: '8px', color: 'red' }}>
                        Informe um e-mail válido para verificação
                      </Typography>
                      <Field
                        as={TextField}
                        variant="outlined"
                        fullWidth
                        id="email"
                        label={i18n.t("signup.form.email")}
                        name="email"
                        autoComplete="email"
                        error={touched.email && Boolean(errors.email)}
                        helperText={
                          (touched.email && errors.email) ? errors.email :
                          (touched.email && !values.email) ? "é obrigatório cadastrar um e-mail válido." : ""
                        }
                        className={classes.textField}
                        onChange={handleEmailChange}
                        value={values.email}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Field
                        as={TextField}
                        variant="outlined"
                        fullWidth
                        id="password"
                        label={i18n.t("signup.form.password")}
                        type="password"
                        name="password"
                        autoComplete="current-password"
                        error={touched.password && Boolean(errors.password)}
                        helperText={touched.password && errors.password}
                        className={classes.textField}
                        onChange={handlePasswordChange}
                        value={values.password}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Field
                        as={TextField}
                        variant="outlined"
                        fullWidth
                        id="phone"
                        label="Contato comercial da empresa"
                        name="phone"
                        autoComplete="phone"
                        error={touched.phone && Boolean(errors.phone)}
                        helperText={
                          (touched.phone && errors.phone) ? errors.phone : ""
                        }
                        className={classes.textField}
                        InputLabelProps={{ style: { fontSize: '0.95rem' } }}
                        inputProps={{ maxLength: 13, inputMode: 'numeric', pattern: '[0-9]*' }}
                        onChange={handlePhoneChange}
                        value={values.phone}
                        InputProps={{}}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        color="secondary"
                        fullWidth
                        onClick={handleOpenPlanModal}
                        className={classes.choosePlanButton}
                      >
                        {selectedPlan ? `Plano Selecionado: ${selectedPlan.name}` : "Escolher Plano"}
                      </Button>
                      {selectedPlan && (
                        <Box className={classes.selectedPlan}>
                          <Typography variant="h6">{selectedPlan.name}</Typography>
                          <Typography variant="body2">
                            Atendentes: {selectedPlan.users} | WhatsApp: {selectedPlan.connections} | Filas: {selectedPlan.queues}
                          </Typography>
                          <Typography variant="h6">R$ {selectedPlan.amount}</Typography>
                        </Box>
                      )}
                      {touched.planId && errors.planId && (
                        <Typography color="error" variant="body2">
                          {errors.planId}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Field
                            as={Checkbox}
                            color="primary"
                            name="termsAccepted"
                            checked={values.termsAccepted}
                          />
                        }
                        label={
                          <Typography variant="body2" className={classes.checkboxLabel}>
                            Eu aceito os{" "}
                            <Link href="#" onClick={(e) => { e.preventDefault(); handleOpenTermsModal(); }}>
                              Termos de Uso
                            </Link>
                          </Typography>
                        }
                      />
                      {touched.termsAccepted && errors.termsAccepted && (
                        <Typography color="error" variant="body2">
                          {errors.termsAccepted}
                        </Typography>
                      )}
                    </Grid>
                  </Grid>

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    color="primary"
                    className={classes.submit}
                    disabled={isSubmitting || !isValid || Object.keys(errors).length > 0 || isValidating}
                  >
                    {isValidating ? "Validando..." : i18n.t("signup.buttons.submit")}
                  </Button>
                  <Grid container justifyContent="flex-end">
                    <Grid item>
                      <Link component={RouterLink} to="/login" variant="body2">
                        {i18n.t("signup.buttons.login")}
                      </Link>
                    </Grid>
                  </Grid>
                </Form>

                {/* Modal para seleção de plano */}
                <Modal
                  open={openPlanModal}
                  onClose={handleClosePlanModal}
                  className={classes.modal}
                  closeAfterTransition
                  BackdropComponent={Backdrop}
                  BackdropProps={{ timeout: 500 }}
                >
                  <Fade in={openPlanModal}>
                    <div className={classes.modalContent}>
                      <IconButton className={classes.closeButton} onClick={handleClosePlanModal} aria-label="Fechar">
                        <CloseIcon />
                      </IconButton>

                      <Typography variant="h5" className={classes.modalTitle}>
                        Escolha um Plano
                      </Typography>
                      <Grid container spacing={3}>
                        {plans.map((plan) => (
                          <Grid item xs={12} sm={6} md={4} key={plan.id}>
                            <Card
                              className={`${classes.card} ${
                                selectedPlan && selectedPlan.id === plan.id ? classes.selectedCard : ""
                              }`}
                            >
                              <div className={classes.cardContainer}>
                                {selectedPlan && selectedPlan.id === plan.id && (
                                  <CheckCircleIcon className={classes.checkIcon} />
                                )}
                                <CardHeader
                                  title={plan.name}
                                  className={classes.cardHeader}
                                  titleTypographyProps={{ align: "center" }}
                                />
                              </div>
                              <CardContent className={classes.cardContent}>
                                <Typography variant="h6" color="textPrimary" align="center">
                                  R$ {plan.amount}
                                </Typography>
                                <Typography variant="body2" color="textSecondary" align="center">
                                  Atendentes: {plan.users}
                                </Typography>
                                <Typography variant="body2" color="textSecondary" align="center">
                                  WhatsApp: {plan.connections}
                                </Typography>
                                <Typography variant="body2" color="textSecondary" align="center">
                                  Filas: {plan.queues}
                                </Typography>
                              </CardContent>
                              <CardActions className={classes.cardActions}>
                                <Button
                                  variant="contained"
                                  color="primary"
                                  onClick={() => handlePlanSelect(plan)}
                                >
                                  {selectedPlan && selectedPlan.id === plan.id ? "Selecionado" : "Selecionar"}
                                </Button>
                              </CardActions>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    </div>
                  </Fade>
                </Modal>

                                {/* Modal para os Termos de Uso */}
                                <Modal
                                    open={openTermsModal}
                                    onClose={handleCloseTermsModal}
                                    className={classes.modal}
                                    closeAfterTransition
                                    BackdropComponent={Backdrop}
                                    BackdropProps={{
                                        timeout: 500,
                                    }}
                                >
                                    <Fade in={openTermsModal}>
                                        <div className={classes.modalContent}>
                                            {/* Botão de fechar */}
                                            <IconButton className={classes.closeButton} onClick={handleCloseTermsModal} aria-label="Fechar">
                                                <CloseIcon />
                                            </IconButton>

                                            <Typography variant="h5" className={classes.modalTitle}>
                                                Termos de Uso - Plataforma de Multi Atendimento {systemName}
                                            </Typography>
                                            <Typography variant="body1" paragraph>
                                                Bem-vindo(a) à nossa plataforma de multi atendimento!
                                            </Typography>
                                            <Typography variant="body1" paragraph>
                                                Ao utilizar nossa plataforma, você concorda com os seguintes termos de uso:
                                            </Typography>
                                            <Typography variant="h6">1. Período de Teste</Typography>
                                            <Typography variant="body1" paragraph>
                                                Oferecemos um período de teste gratuito de 7 dias para todos os novos clientes. Durante esse período, você pode testar nossa plataforma sem nenhum custo. Se você optar por se tornar um cliente pago durante o período de teste, terá direito a um reembolso total do valor pago caso não esteja satisfeito(a) com nossos serviços.
                                            </Typography>
                                            <Typography variant="body1" paragraph>
                                                Após o término do período de teste, não será mais possível solicitar um reembolso.
                                            </Typography>
                                            <Typography variant="h6">2. Uso da Plataforma</Typography>
                                            <Typography variant="body1" paragraph>
                                                Ao utilizar nossa plataforma, você concorda em não utilizar nossos serviços para práticas ilícitas, como envio de spam, atividades fraudulentas ou qualquer outra atividade ilegal.
                                            </Typography>
                                            <Typography variant="body1" paragraph>
                                                Reservamo-nos o direito de suspender ou encerrar a conta de qualquer usuário que viole nossos termos de uso ou utilize a plataforma de forma inadequada.
                                            </Typography>
                                            <Typography variant="h6">3. Compatibilidade</Typography>
                                            <Typography variant="body1" paragraph>
                                                Nossa plataforma de multi atendimento é compatível com dispositivos móveis e computadores desktop. Isso permite que você acesse e utilize nossos serviços de forma conveniente em qualquer lugar.
                                            </Typography>
                                            <Typography variant="h6">4. Política de Privacidade e Segurança</Typography>
                                            <Typography variant="body1" paragraph>
                                                Nossa plataforma implementa medidas de segurança para garantir a proteção dos seus dados. Todas as informações pessoais são tratadas de forma criptografada e armazenadas de maneira segura, utilizando os mais altos padrões de segurança para evitar qualquer vazamento ou acesso não autorizado.
                                            </Typography>
                                            <Typography variant="body1" paragraph>
                                                Para mais informações sobre como coletamos, usamos e protegemos suas informações pessoais, consulte nossa Política de Privacidade.
                                            </Typography>
                                            <Typography variant="h6">5. Alterações nos Termos de Uso</Typography>
                                            <Typography variant="body1" paragraph>
                                                Reservamo-nos o direito de modificar ou atualizar estes termos de uso a qualquer momento. Recomendamos que você revise periodicamente esta página para estar ciente de quaisquer alterações que possam ocorrer.
                                            </Typography>

                                            <Typography variant="h5" className={classes.modalTitle}>
                                                Política de Privacidade - Plataforma de Multi Atendimento {systemName}
                                            </Typography>
                                            <Typography variant="body1" paragraph>
                                                Agradecemos por escolher utilizar nossa plataforma de Multi Atendimento {systemName}. Nesta Política de Privacidade, explicamos como coletamos, usamos, compartilhamos e protegemos as informações pessoais fornecidas por você durante o uso de nossa plataforma.
                                            </Typography>
                                            <Typography variant="h6">1. Informações Coletadas</Typography>
                                            <Typography variant="body1" paragraph>
                                                Podemos coletar informações pessoais que você fornece voluntariamente ao utilizar nossa plataforma. Isso pode incluir, mas não está limitado a:
                                            </Typography>
                                            <ul>
                                                <li>Nome completo;</li>
                                                <li>Endereço de e-mail;</li>
                                                <li>Número de telefone;</li>
                                                <li>Informações de pagamento;</li>
                                                <li>Outras informações que você fornece ao interagir com nossa plataforma.</li>
                                            </ul>
                                            <Typography variant="h6">2. Uso das Informações</Typography>
                                            <Typography variant="body1" paragraph>
                                                As informações pessoais coletadas são utilizadas para os seguintes fins:
                                            </Typography>
                                            <ul>
                                                <li>Fornecer e melhorar nossos serviços;</li>
                                                <li>Processar pagamentos e garantir a efetivação de suas transações;</li>
                                                <li>Enviar notificações importantes relacionadas à sua conta e aos serviços que oferecemos;</li>
                                                <li>Comunicar informações sobre atualizações, novidades e ofertas especiais (com seu consentimento, quando necessário);</li>
                                                <li>Entender melhor suas necessidades e interesses para melhorar nossos serviços;</li>
                                                <li>Responder a suas perguntas e solicitações de suporte.</li>
                                            </ul>
                                            <Typography variant="h6">8. Contato</Typography>
                                            <Typography variant="body1" paragraph>
                                                Se você tiver alguma dúvida ou preocupação sobre nossa política de privacidade ou sobre o uso de suas informações pessoais, entre em contato conosco através do seguinte meio:
                                            </Typography>
                                            <ul>
                                                <li>WhatsApp: (92)9305-6193</li>
                                                <li>E-mail: suporte@workzap.online</li>
                                            </ul>
                                            <Typography variant="body1" paragraph>
                                                Ao utilizar nossa plataforma, você concorda com os termos e práticas descritos nesta política de privacidade.
                                            </Typography>
                                        </div>
                                    </Fade>
                                </Modal>

                                {/* Modal de Carregamento */}
                <Modal
                  open={isSubmitting}
                  className={classes.modal}
                  closeAfterTransition
                  BackdropComponent={Backdrop}
                  BackdropProps={{ timeout: 500 }}
                >
                  <Fade in={isSubmitting}>
                    <div className={classes.loadingModalContent}>
                      <CircularProgress />
                      <Typography variant="h6" style={{ marginTop: "16px" }}>
                        Registrando...
                      </Typography>
                    </div>
                  </Fade>
                </Modal>
              </>
            );
          }}
        </Formik>
      </div>

      <Box mt={5} />
    </Container>
  );
};

export default SignUp;
